import { LocationType, OrderStatus, PdvAccountStatus } from '@prisma/client';

import { OrderSummaryItem } from '../order';

/**
 * Contrato do **painel de contas** da plataforma (F13) — respostas de `GET /accounts`,
 * `GET /accounts/:id` e dos três verbos (`POST /accounts`, `POST /accounts/:id/close`,
 * `POST /accounts/:id/transfer`). Autenticação por JWT de usuário; o escopo de empresa vem sempre do
 * token, nunca de parâmetro.
 *
 * É contrato de PLATAFORMA, separado do de integração (`IntegrationLocationAccount`) de propósito: o
 * parceiro não recebe o nosso `id` (a chave dele é `externalAccountId`), e o operador precisa
 * exatamente do `id` — é a chave de rota dos verbos. Fundir os dois faria o campo interno vazar para
 * o contrato publicado.
 *
 * 🔒 O `slug` da location **nunca** aparece aqui (D162): ele é a credencial do QR, e quem o mostra é a
 * tela de locations, mesa a mesa.
 */

/** Valores aceitos no filtro `status` da listagem. Ausente, a listagem responde só as `OPEN`. */
export const ACCOUNT_PANEL_STATUS_FILTERS = [PdvAccountStatus.OPEN, PdvAccountStatus.CLOSED, 'ALL'] as const;

export type AccountPanelStatusFilter = (typeof ACCOUNT_PANEL_STATUS_FILTERS)[number];

/**
 * Onde a conta está — resolvido contra o cadastro **atual** da location, não contra o snapshot da
 * abertura: depois de uma renumeração o snapshot aponta para uma mesa que não existe mais, e o
 * operador age sobre a mesa de hoje. Snapshot só como fallback, quando a location foi apagada.
 *
 * Tudo anulável porque conta legada pode não ter `locationId` ("conta de mesa não mapeada", que o
 * schema permite desde a F0) e a location pode ter sido removida do cadastro.
 */
export interface AccountPanelLocation {
  id: string | null;
  number: string | null;
  type: LocationType | null;
  pointOfSaleId: string | null;
  pointOfSaleName: string | null;
}

export interface AccountPanelSummary {
  /** ObjectId da conta — a chave de rota dos verbos do painel. */
  id: string;
  status: PdvAccountStatus;
  /**
   * `PdvAccounts.total` como está no banco — o autoritativo, aditivo, que o PDV externo também
   * alimenta (D10/D32). Nunca recomputado, nunca editável por superfície nenhuma (D177).
   */
  total: number;
  /** Chave da conta no PDV/PMS do parceiro. `null` = conta aberta manualmente pelo painel (D169). */
  externalAccountId: string | null;
  /**
   * Nome do hóspede desta conta (F14/D191) — o que dá sentido à comanda: "Comanda 12 · João". `null`
   * quando nem o PMS nem o operador informaram.
   */
  guestName: string | null;
  location: AccountPanelLocation;
  /** ISO UTC — a tela formata, como no painel de pedidos. */
  openedAt: string;
  /** Instante do fechamento (nada escreve em conta fechada). `null` enquanto `OPEN`. */
  closedAt: string | null;
}

/** Lista paginada de `GET /accounts` — `page` 1-based, página default de 50 (a mesma da integração). */
export interface AccountPanelListResponse {
  data: AccountPanelSummary[];
  page: number;
  limit: number;
  total: number;
}

/**
 * Um pedido no detalhe da conta — projeção de OPERADOR, mais larga que a do hóspede de propósito
 * (D179): inclui `status` (cancelado aparece marcado, para auditoria) e `syncedAt` (o diálogo de
 * fechamento avisa quando há pedido que o PDV ainda não puxou). Continua allowlist: nada de
 * `idempotencyKey`, `sessionId` ou coordenadas.
 */
export interface AccountPanelOrder {
  /** ObjectId do pedido — navegação para o painel de pedidos. */
  id: string;
  /** O nanoId: a chave do pedido, que desde a F15 aparece ao lado do número curto. */
  number: string;
  /** F15/D205 — número curto do dia (`#042`), o que o caixa e a cozinha falam; `null` em pedido anterior à F15. */
  dailyNumber: number | null;
  /** ISO UTC. */
  createdAt: string;
  status: OrderStatus;
  /** ISO UTC, ou `null` se o PDV ainda não confirmou a sincronia. */
  syncedAt: string | null;
  customerName: string | null;
  total: number;
  items: OrderSummaryItem[];
}

/**
 * 🔴 As DUAS verdades sobre o dinheiro da conta, lado a lado e sem fundir (D174) — todas calculadas
 * no servidor, com a conta feita em centavos inteiros e devolvidas em REAIS. A tela nunca soma.
 */
export interface AccountPanelTotals {
  /** `PdvAccounts.total` — o que o hotel cobra. */
  accountTotal: number;
  /** Soma dos NOSSOS pedidos faturáveis (`ORDER_BILLABLE_STATUSES`) — o que passou pelo cardápio. */
  ordersTotal: number;
  /**
   * `accountTotal − ordersTotal`: consumo lançado direto no terminal do PDV. É informação rotulada,
   * nunca "erro a corrigir" — e pode ser negativo (ajuste feito no PDV, dado legado).
   */
  externalDelta: number;
}

/** Outra perna `OPEN` da mesma conta compartilhada (D92) — o diálogo de fechar/transferir avisa. */
export interface AccountPanelSharedLeg {
  id: string;
  locationNumber: string | null;
}

export interface AccountPanelDetail extends AccountPanelSummary {
  /**
   * TODOS os status, cancelado marcado (D179), ordenados por `createdAt` com desempate por `number`.
   * Sem paginação, como o extrato do hóspede: a conta vive uma ocupação — dezenas de pedidos, não
   * milhares.
   */
  orders: AccountPanelOrder[];
  totals: AccountPanelTotals;
  sharedAccounts: AccountPanelSharedLeg[];
  /**
   * A empresa tem ao menos uma `Integration` **ativa** — o mesmo sinal que o painel de pedidos publica
   * (`OrderBoardResponse.syncTracked`), aqui porque esta tela também fala de sincronia: a coluna
   * "Sincronizado" de cada pedido e o aviso de fechamento com pedido que o PDV não puxou.
   *
   * 🔴 Sem credencial ativa **ninguém pode** marcar `syncedAt` (só a rota autenticada de
   * `/integration` escreve o campo), então a coluna marcaria `—` em todo pedido e o diálogo avisaria
   * de uma pendência impossível de resolver — num aviso que o operador lê antes de fechar dinheiro.
   * A coluna segue só este sinal (`=== true`), como o indicador do painel. O aviso de fechamento tem
   * portão próprio, que também olha os dados da conta — ver `docs/regras-de-dominio.md`.
   */
  syncTracked: boolean;
}

/**
 * Resposta de `POST /accounts/:id/close`. `closedCount` é quantos documentos ESTA chamada fechou —
 * pernas compartilhadas incluídas (D170/D92). `0` = replay de conta que já estava fechada, que é
 * `200` de propósito: repetir um fechamento que valeu não é erro para o caixa.
 */
export interface AccountPanelCloseResult {
  account: AccountPanelSummary;
  closedCount: number;
}
